import { Navigate, Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface RevenueEventResponse {
  record_id: string;
  stripe_event_id: string;
  customer_id: string;
  product_slug: string;
  amount_usd_cents: number;
  amount_ils_agorot: number;
  vat_status: string;
  vat_amount_agorot: number;
  net_revenue_agorot: number;
  payment_type: string;
  customer_country: string;
  received_at: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtIls(agorot: number): string { return '₪' + (agorot / 100).toFixed(2); }
export function fmtUsd(cents: number): string { return '$' + (cents / 100).toFixed(2); }
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function fmtVatStatus(s: string): string {
  return s === 'applicable' ? 'VAT liable' : s === 'exempt' ? 'Exempt' : s === 'reverse_charge' ? 'Reverse Charge' : s;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FieldRow({ label, children, mono, highlight }: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  highlight?: boolean;
}) {
  return (
    <div style={{
      display: 'flex', gap: 12,
      padding: '10px 0',
      borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
      alignItems: 'flex-start',
      background: highlight ? 'rgba(125,211,252,0.04)' : 'transparent',
    }}>
      <span style={{
        flexShrink: 0, width: 180,
        fontSize: '8.5pt', color: 'var(--omega-ink-subtle)',
        letterSpacing: '0.06em', textTransform: 'uppercase',
        paddingTop: 2, fontFamily: 'var(--omega-mono)',
      }}>
        {label}
      </span>
      <span style={{
        fontSize: '9.5pt',
        color: highlight ? '#7dd3fc' : 'var(--omega-ink)',
        fontFamily: mono ? 'var(--omega-mono)' : 'var(--omega-sans)',
        fontWeight: highlight ? 700 : 400,
      }}>
        {children}
      </span>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceRevenueEventDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { event_id } = useParams<{ event_id: string }>();

  const { data: event, isPending, isError } = useQuery({
    queryKey: ['finance', 'revenue', 'event', event_id],
    queryFn: () => apiFetch<RevenueEventResponse>(`/finance/revenue/events/${event_id ?? ''}`),
    enabled: !!event_id,
    refetchInterval: 30_000,
  });

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Revenue', to: '/omega/finance/revenue' },
        { label: 'Events', to: '/omega/finance/revenue/events' },
        { label: event_id ?? '…' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Revenue event
        </h1>
        <Link
          to="/omega/finance/revenue/events"
          style={{
            marginLeft: 'auto',
            background: 'rgba(125,211,252,0.12)',
            border: '1px solid rgba(125,211,252,0.35)',
            borderRadius: 4, color: '#7dd3fc', fontFamily: 'var(--omega-mono)',
            fontSize: '8.5pt', fontWeight: 600, padding: '5px 12px',
            textDecoration: 'none', letterSpacing: '0.04em',
          }}
        >
          ← Back to events
        </Link>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {event && (
        <div style={{
          background: 'var(--omega-surface, rgba(255,255,255,0.04))',
          border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
          borderRadius: 6, padding: '0 18px',
        }}>
          <FieldRow label="Record ID" mono>{event.record_id}</FieldRow>
          <FieldRow label="Stripe Event ID" mono>{event.stripe_event_id}</FieldRow>
          <FieldRow label="Customer" mono>{event.customer_id}</FieldRow>
          <FieldRow label="Receipt date">{fmtDate(event.received_at)}</FieldRow>
          <FieldRow label="Payment type" mono>{event.payment_type}</FieldRow>
          <FieldRow label="Amount USD" mono>{fmtUsd(event.amount_usd_cents)}</FieldRow>
          <FieldRow label="Amount ILS" mono highlight>{fmtIls(event.amount_ils_agorot)}</FieldRow>
          <FieldRow label="VAT status">
            {fmtVatStatus(event.vat_status)}
          </FieldRow>
          <FieldRow label="VAT amount" mono>{fmtIls(event.vat_amount_agorot)}</FieldRow>
          <FieldRow label="Net revenue" mono>{fmtIls(event.net_revenue_agorot)}</FieldRow>
          <FieldRow label="Customer country" mono>{event.customer_country}</FieldRow>
          <FieldRow label="Product">{event.product_slug}</FieldRow>
        </div>
      )}
    </div>
  );
}
